/**
 * IUPAC 検索（§5.4）。両鎖・環状。ヒット上限は呼び出し側。
 */

import type { Interval, Topology } from '../sequence/interval';
import { resolveSpans } from '../sequence/interval';
import { codeMatchesIupac, encodeIupacPattern, reverseComplementIupac } from './iupac';

export interface PatternHit {
  start: number;
  end: number;
  strand: 1 | -1;
}

export interface SearchPatternsOptions {
  genomeLength: number;
  topology: Topology;
  interval: Interval;
  pattern: string;
  bothStrands?: boolean;
  limit?: number;
}

export function searchCodes(
  codes: Uint8Array,
  options: SearchPatternsOptions
): PatternHit[] {
  const pattern = options.pattern.replace(/\s+/g, '');
  const forward = encodeIupacPattern(pattern);
  if (!forward || forward.length === 0) return [];

  const reverse = options.bothStrands === false ? null : encodeIupacPattern(reverseComplementIupac(pattern));
  const limit = options.limit ?? 2000;
  const hits: PatternHit[] = [];
  const spans = resolveSpans(options.interval, options.genomeLength, options.topology);

  for (const span of spans) {
    scanSpan(codes, span.start, span.end, forward, 1, hits, limit);
    if (hits.length >= limit) break;
    if (reverse) {
      scanSpan(codes, span.start, span.end, reverse, -1, hits, limit);
      if (hits.length >= limit) break;
    }
  }

  const coversOrigin =
    options.topology === 'circular' &&
    (options.interval.end > options.genomeLength ||
      (options.interval.start === 0 && options.interval.end >= options.genomeLength));
  if (coversOrigin && options.interval.end <= options.genomeLength) {
    // 全長走査のときだけ原点またぎを足す（ビューポート外の尾を混ぜない）
    const wrapHits = scanCircularWrap(codes, options.genomeLength, forward, 1, limit - hits.length);
    hits.push(...wrapHits);
    if (reverse && hits.length < limit) {
      hits.push(...scanCircularWrap(codes, options.genomeLength, reverse, -1, limit - hits.length));
    }
  }

  hits.sort((a, b) => a.start - b.start || a.strand - b.strand);
  return hits.slice(0, limit);
}

function scanSpan(
  codes: Uint8Array,
  start: number,
  end: number,
  pattern: Uint8Array,
  strand: 1 | -1,
  hits: PatternHit[],
  limit: number
): void {
  const last = end - pattern.length;
  for (let pos = start; pos <= last; pos += 1) {
    if (matchesAt(codes, pos, pattern)) {
      hits.push({ start: pos, end: pos + pattern.length, strand });
      if (hits.length >= limit) return;
    }
  }
}

function scanCircularWrap(
  codes: Uint8Array,
  genomeLength: number,
  pattern: Uint8Array,
  strand: 1 | -1,
  remaining: number
): PatternHit[] {
  if (pattern.length < 2 || remaining <= 0) return [];
  const hits: PatternHit[] = [];
  for (let overhang = 1; overhang < pattern.length; overhang += 1) {
    const start = genomeLength - overhang;
    if (matchesAtWrapped(codes, start, pattern, genomeLength)) {
      hits.push({ start, end: start + pattern.length, strand });
      if (hits.length >= remaining) break;
    }
  }
  return hits;
}

function matchesAt(codes: Uint8Array, start: number, pattern: Uint8Array): boolean {
  for (let i = 0; i < pattern.length; i += 1) {
    if (!codeMatchesIupac(codes[start + i], pattern[i])) return false;
  }
  return true;
}

function matchesAtWrapped(
  codes: Uint8Array,
  start: number,
  pattern: Uint8Array,
  genomeLength: number
): boolean {
  for (let i = 0; i < pattern.length; i += 1) {
    const pos = start + i < genomeLength ? start + i : start + i - genomeLength;
    if (!codeMatchesIupac(codes[pos], pattern[i])) return false;
  }
  return true;
}
